/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { PreviewKernel, PreviewKernelType } from 'client-code/actions';
import {
    CriticRating,
    CriticRatingSource,
    InternalRating,
    OnePassItem,
    PreviewType,
    RecordingAvailability,
    RecordingStatus,
    TrickPlay,
} from 'client-code/previews';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../config';
import { DeviceModel } from '../../../core/device';
import {
    TContentPreview,
    TOfferPreview,
    TOnePassOptions,
    TOnePassPreviewItem,
    TPreviewKernel,
    TPreviewKernelType,
    TRecordingOptionsPreview,
    TUnifiedCollectionPreview,
    TVodOfferPreview,
} from '../../../core/types';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import { DateTime } from '../../../core/utils';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { VodPreviewFormatter } from '../../common/VodPreview/VodPreviewFormatter';
import { VodScreenType } from '../../common/VodPreview/VodPreviewProps';

import './ContentPreviewComponent.scss';

interface ContentPreviewProps {
    previewKernel: PreviewKernel | TPreviewKernel;
}

const ContentPreviewComponent: FunctionComponent<ContentPreviewProps> = ({
    previewKernel,
}): JSX.Element => {
    /**
     * Debounce time (in milliseconds) for processing the preview kernel.
     * This delay helps debounce the processing function, ensuring it is not called too frequently,
     * and provides a buffer period for asynchronous operations to complete.
     */
    const PREVIEW_DEBOUNCE_TIME = 800;

    const [contentPreviewInfo, setContentPreviewInfo] = useState<TContentPreview>();
    const [offerPreviewInfo, setOfferPreviewInfo] = useState<TOfferPreview>();
    const [onePassOptionsPreviewInfo, setOnePassOptionsPreviewInfo] =
        useState<TOnePassPreviewItem>();
    const [previewKernelType, setPreviewKernelType] = useState<
        PreviewKernelType | TPreviewKernelType
    >();
    const [recordingOptionsPreviewInfo, setRecordingOptionsPreviewInfo] =
        useState<TRecordingOptionsPreview>();
    const [recordingPreviewInfo, setRecordingPreviewInfo] = useState<TOfferPreview>();
    const [unicAndOnePassOptionsPreviewInfo, setUnicAndOnePassOptionsPreviewInfo] =
        useState<TOnePassOptions>();
    const [unicPreviewInfo, setUnicPreviewInfo] = useState<TUnifiedCollectionPreview>();
    const [vodOfferPreviewInfo, setVodOfferPreviewInfo] = useState<TVodOfferPreview>();

    const useCases: PreviewComponent = new PreviewComponent(previewKernel);
    const oPreviewAssets: PreviewComponentAssets = useCases.getPreviewComponentAssets();

    /**
     * Part 0 - Thuuz rating
     * @param criticRating - The critic rating object containing the rating value and source.
     * @param isLive - Optional boolean indicating if the content is live.
     * @returns `JSX.Element`
     */
    const ThuzzRating: FunctionComponent<{ criticRating?: CriticRating; isLive?: boolean }> = ({
        criticRating,
        isLive,
    }) => {
        if (!criticRating) return null;
        const { source, value } = criticRating;
        if (!source || !value || source !== CriticRatingSource.Thuuz) return null;
        // <Thuuz rating icon> <meaning></meaning>
        const thuzzRatingAssets = oPreviewAssets.getThuzzRatingAssets(value);
        return (
            <React.Fragment>
                <div className="thuzz-rating">
                    <span
                        className={
                            isLive
                                ? `${thuzzRatingAssets.ratingClass} started`
                                : thuzzRatingAssets.ratingClass
                        }
                    >
                        {value}
                    </span>{' '}
                    {thuzzRatingAssets.meaning}
                </div>
            </React.Fragment>
        );
    };

    /**
     * Part 1 - teams, series and league - SportsPass Event OnePass Biaxial Preview
     * @param param
     * @returns `JSX.Element`
     */
    const TeamSeriesAndLeague: FunctionComponent<{
        onePassIndicatorList?: Array<OnePassItem>;
        league?: OnePassItem;
        skipIfTitleMatch?: string;
    }> = ({ onePassIndicatorList, league, skipIfTitleMatch }) => {
        if (!onePassIndicatorList) return null;
        const statusIconUrl: string = oPreviewAssets.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.ICON_STATUS_ONEPASS,
        );

        const onepassOptions = onePassIndicatorList.map((item) => {
            if (item.title === skipIfTitleMatch) return null;
            if (item.title === league?.title) {
                return (
                    <div className="teams-series-and-league" key={item.title}>
                        {`${item.title} (${item.postfixTitle})`}
                        {item.hasOnePass && <img src={statusIconUrl} />}
                    </div>
                );
            }
            return (
                <div className="teams-series-and-league" key={item.title}>
                    {item.title}
                    {item.hasOnePass && <img src={statusIconUrl} />}
                </div>
            );
        });

        return <React.Fragment>{onepassOptions}</React.Fragment>;
    };

    /**
     * Part 1 - "<status message>"
     * @param params
     * @returns `JSX.Element`
     */
    const StatusMessage: FunctionComponent<{
        entitlementAndAvailabilityStatus?: string;
        status?: string;
        isKeepForeverRecording?: boolean;
        showType?: string;
        isPartialRecording?: boolean;
        startTime?: string;
        endTime?: string;
        duration?: number;
        recordingAvailability?: RecordingAvailability;
    }> = ({
        entitlementAndAvailabilityStatus,
        status,
        isKeepForeverRecording,
        showType,
        isPartialRecording,
        startTime,
        endTime,
        duration,
        recordingAvailability,
    }) => {
        if (!entitlementAndAvailabilityStatus && !status && !isPartialRecording) return null;

        let assets = { stringAsset: '', imageAssetUrl: '' };
        // Use Cases for VOD status messages
        if (entitlementAndAvailabilityStatus) {
            assets.stringAsset = entitlementAndAvailabilityStatus;
        } else {
            // Use Cases for Recording status messages
            // Use Case 1: <recording now icon> This <show type> is currently recording
            assets = oPreviewAssets.getRecordingStatusAssets(
                status,
                showType,
                isKeepForeverRecording,
            );

            // Use Case 2: <partial icon> Partial recording <start time> - <end time> | <duration>
            if (
                status === RecordingStatus.CompleteRecording &&
                isPartialRecording &&
                startTime &&
                endTime &&
                duration
            ) {
                assets = oPreviewAssets.getPartialRecordingStatusAssets(
                    startTime,
                    endTime,
                    duration,
                );
            }

            // Use Case 3: <not recordable cell icon> Channel does not allow recording.
            if (
                recordingAvailability &&
                recordingAvailability !== RecordingAvailability.RecordingAllowed
            ) {
                assets = oPreviewAssets.getRecordingAvailabilityAssets(recordingAvailability);
            }
        }

        const imageSrc = assets.imageAssetUrl;

        return (
            <React.Fragment>
                <div className="status-message">
                    {imageSrc ? <img src={imageSrc} /> : null} <span>{assets.stringAsset}</span>
                </div>
            </React.Fragment>
        );
    };

    /**
     * Part 1aa - "<trickplay restriction string>"
     * @param param
     * @returns `JSX.Element`
     */
    const TrickPlayRestriction: FunctionComponent<{
        restrictions?: Array<TrickPlay>;
        isLive?: boolean;
    }> = ({ restrictions, isLive }) => {
        if (!restrictions?.length) return null;
        // <list of restricted TrickPlay modes> <is/are> not <supported/allowed>
        const restrictionAssets = oPreviewAssets.getTrickplayRestrictionAssets(
            restrictions,
            isLive,
        );

        const iconSrc = restrictionAssets.imageAssetUrl;

        return (
            <React.Fragment>
                <div className="trickplay-restriction">
                    {iconSrc ? <img src={iconSrc} /> : null}
                    {`${restrictionAssets.stringAsset}`}
                </div>
            </React.Fragment>
        );
    };

    /**
     * Part 1a - Time airing info
     * @returns
     */
    const TimeAiringInfo: FunctionComponent<{
        startTime?: string;
        endTime?: string;
    }> = ({ startTime, endTime }) => {
        if (!(startTime && endTime)) return null;
        // <date> <start time> - <end time>
        const oAiredDate = new DateTime(startTime);
        const oStartTime = new DateTime(startTime);
        const oEndTime = new DateTime(endTime);
        return (
            <React.Fragment>
                <div className="time-airing-info">
                    {oAiredDate.strftime('%a %m/%D', DeviceModel.getTVProperties().getLang())}{' '}
                    {oStartTime.to12HourClock()} {' - '}
                    {oEndTime.to12HourClock()}
                </div>
            </React.Fragment>
        );
    };

    /**
     * Part 1b - Channel airing info
     * @returns `JSX.Element`
     */
    const ChannelAiringInfo: FunctionComponent<{
        channelNumber?: string;
        callSign?: string;
        videoResolution?: string;
        isThreeD?: boolean;
        isPpv?: boolean;
    }> = ({ channelNumber, callSign, videoResolution, isThreeD, isPpv }) => {
        // <channel number> <format icon> <call sign> <ppv icon>

        let formatIconUrl = '';
        if (isThreeD) {
            formatIconUrl = oPreviewAssets.getImageAssetUrlById(
                PreviewComponentAssets.ImageAssets.THREE_D_ICON,
            );
        }
        if (!isThreeD && videoResolution) {
            formatIconUrl = oPreviewAssets.getImageAssetUrlById(
                `${videoResolution.toLocaleUpperCase()}_ICON`,
            );
        }

        let ppvIconUrl = '';
        if (isPpv) {
            ppvIconUrl = oPreviewAssets.getImageAssetUrlById(
                PreviewComponentAssets.ImageAssets.PPV_ICON,
            );
        }

        return (
            <React.Fragment>
                <div className="channel-airing-info">
                    {channelNumber} {formatIconUrl ? <img src={formatIconUrl} /> : null} {callSign}{' '}
                    {ppvIconUrl ? <img src={ppvIconUrl} /> : null}
                </div>
            </React.Fragment>
        );
    };

    /**
     * Part 2 - "<series / movie title / non-episodic / music title> (<year>)"
     * @returns `JSX.Element`
     */
    const ContentPreviewTitle: FunctionComponent<{
        title?: string;
        year?: number;
        showType?: string;
    }> = ({ title, year, showType }) => {
        return (
            <React.Fragment>
                <div className="content-preview-title">
                    {title}
                    {showType === PreviewType.MoviePreview ? ` (${year})` : null}
                </div>
            </React.Fragment>
        );
    };

    /**
     * Part 3 - "<sea/ep> <episode title>"
     * @returns `JSX.Element`
     */
    const SeaEpEpisodeTitle: FunctionComponent<{
        seasonNumber?: number;
        episodeNumber?: number;
        episodeTitle?: string;
        showType?: string;
    }> = ({ seasonNumber, episodeNumber, episodeTitle, showType }) => {
        if (showType === PreviewType.MoviePreview || showType === PreviewType.ProgramPreview)
            return null;
        const season = seasonNumber ? `S${seasonNumber}` : '';
        const episode = episodeNumber ? `E${episodeNumber}` : '';
        const seaEp = `${season} ${episode}`.trim();

        return (
            <React.Fragment>
                <div className="seaep-episode-title">
                    {seaEp ? (
                        <>
                            <span>{seaEp}</span> {LEFT_QUOTE}
                            {episodeTitle}
                            {RIGHT_QUOTE}
                        </>
                    ) : (
                        <>
                            {LEFT_QUOTE}
                            {episodeTitle}
                            {RIGHT_QUOTE}
                        </>
                    )}
                </div>
            </React.Fragment>
        );
    };

    /**
     * Part 4 - "<episode / movie / non-episodic / series / music description> <(show attributes)>
     * <button INFO icon>"
     * @returns `JSX.Element`
     */
    const ContentDescription: FunctionComponent<{
        description?: string;
        videoResolution?: string;
        hasCc?: boolean;
        isThreeD?: boolean;
        hasSap?: boolean;
    }> = ({
        description = '',
        videoResolution = '',
        hasCc = false,
        hasSap = false,
        isThreeD = false,
    }) => {
        const showAttributes: Array<string> = [];
        if (hasCc) {
            showAttributes.push('CC');
        }
        if (hasSap) {
            showAttributes.push('SAP');
        }
        if (isThreeD) {
            showAttributes.push('3D');
        }
        if (!isThreeD && videoResolution) {
            showAttributes.push(videoResolution.toLocaleUpperCase());
        }

        const infoIconUrl = oPreviewAssets.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.BUTTON_INFO_ICON,
        );

        const [shouldTrimDescription, setShouldTrimDescription] = useState(false);

        const descriptionRef = useRef<HTMLDivElement>(null);
        const containerClass = `content-description ${shouldTrimDescription ? 'has-trimmed-description' : ''}`;

        useLayoutEffect(() => {
            if (!descriptionRef.current || description.length === 0) return;

            const maxHeight = window.innerHeight * (144 / 1080); // 13.33% of viewport height
            const contentHeight = descriptionRef.current.clientHeight;
            setShouldTrimDescription(contentHeight > maxHeight);
        }, [description]);

        const calculateMaskSizeClass = (detailsString: string): string => {
            if (detailsString.length > 0 && detailsString.length < 7) {
                return 'mask-size-small';
            }
            if (detailsString.length >= 7 && detailsString.length < 12) {
                return 'mask-size-medium';
            }
            if (detailsString.length >= 12) {
                return 'mask-size-large';
            }
            return '';
        };

        const maskSizeClassName = calculateMaskSizeClass(showAttributes.join(', '));

        return (
            <div className={containerClass}>
                <div
                    className={`show-description ${maskSizeClassName}`}
                    data-show-details={
                        showAttributes?.length ? ` (${showAttributes.join(', ')})` : ''
                    }
                    style={{ '--info-icon-url': `url(${infoIconUrl})` } as React.CSSProperties}
                    ref={descriptionRef}
                >
                    {description}
                </div>

                {showAttributes.length ? (
                    <span className="show-attributes">
                        {`(${showAttributes.join(', ')})`}{' '}
                        <img className="preview-attributes-icon" src={infoIconUrl} />
                    </span>
                ) : null}
            </div>
        );
    };

    /**
     * Part 5 - "<parental control rating> <genre(s)>"
     * @returns `JSX.Element`
     */
    const ParentalControlRatingAndGenres: FunctionComponent<{
        internalRatings?: Array<InternalRating>;
        genres?: string;
    }> = ({ internalRatings = [], genres = '' }) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const [isOverflowed, setIsOverflowed] = useState(false);

        useEffect(() => {
            if (containerRef.current) {
                const isElementOverflowed =
                    containerRef.current.scrollHeight > containerRef.current.clientHeight;
                setIsOverflowed(isElementOverflowed);
            }
        }, [internalRatings, genres]); // Re-check when content changes

        const ratingNames = PreviewComponent.getParentalRatingNames(internalRatings)
            .filter((name) => name)
            .map((rating) => {
                return (
                    <span className="rating" key={rating}>
                        {rating}
                    </span>
                );
            });

        return (
            <React.Fragment>
                <div ref={containerRef} className="parental-control-rating-genres">
                    {ratingNames}{' '}
                    <span className={`genres${isOverflowed ? ' overflow' : ''}`}>{genres}</span>
                </div>
            </React.Fragment>
        );
    };

    /**
     * Part 6 - "<day of week> <date> | <duration> | <star rating> | <First aired / First airs>:
     * <airdate>"
     * @returns `JSX.Element`
     */
    const AiringDateTimeDuration: FunctionComponent<{
        duration?: number;
        starRating?: number;
        firstAiredDate?: string;
    }> = ({ duration = 0, starRating = 0, firstAiredDate = '' }) => {
        if (!(duration || starRating || firstAiredDate)) return null;
        const starRatingNames = oPreviewAssets.getStarRatingNames(starRating);

        const getStarImges = () => {
            return starRatingNames.map((name, index) => {
                if (name === 'full') return <img key={index} src={fullStarUrl} />;
                if (name === 'half') return <img key={index} src={halfStarUrl} />;
                return <img key={index} src={emptyStarUrl} />;
            });
        };

        const narrateAiringDateTimeDuration = () => {
            return (
                <React.Fragment>
                    {DateTime.secondsToString(duration)}
                    {starRating ? <React.Fragment> | {getStarImges()}</React.Fragment> : null}
                    {firstAiredDate
                        ? ` | ${oPreviewAssets.getFirstArisOrAiredString(firstAiredDate)}`
                        : null}
                </React.Fragment>
            );
        };

        const emptyStarUrl = oPreviewAssets.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.EMPTY_STAR_ICON,
        );

        const fullStarUrl = oPreviewAssets.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.FULL_STAR_ICON,
        );

        const halfStarUrl = oPreviewAssets.getImageAssetUrlById(
            PreviewComponentAssets.ImageAssets.HALF_STAR_ICON,
        );

        return (
            <React.Fragment>
                <div className="airing-datetime-duration">{narrateAiringDateTimeDuration()}</div>
            </React.Fragment>
        );
    };

    /**
     * Part 8a - "<actors in billing order>"
     * @returns `JSX.Element`
     */
    const ActiorsInBillingOrder: FunctionComponent<{ creditString?: string }> = ({
        creditString = '',
    }) => {
        const credits = /actor:(.+?)~/.exec(creditString);
        return (
            <React.Fragment>
                {credits ? <div className="actor-in-billing-order">{credits[1]}</div> : null}
            </React.Fragment>
        );
    };

    /**
     * Creates elements displaying time and channel airing information based on the provided
     * preview information.
     *
     * @param previewInfo - The information used to create the time and channel airing info
     * elements.
     * @returns A JSX element representing the time and channel airing info.
     */
    const createTimeAndChannelAiringInfoElements = (previewInfo): JSX.Element => {
        return (
            <React.Fragment>
                <TimeAiringInfo startTime={previewInfo.startTime} endTime={previewInfo.endTime} />
                <ChannelAiringInfo
                    channelNumber={previewInfo.channel?.channelNumber}
                    videoResolution={previewInfo.channel?.videoResolution}
                    callSign={previewInfo.channel?.callSign}
                    isPpv={previewInfo.isPpv}
                    isThreeD={previewInfo.isThreeD}
                />
            </React.Fragment>
        );
    };

    /**
     * Creates common preview elements based on the provided preview information.
     *
     * @param previewInfo - The information used to create the common preview elements.
     * @returns A JSX element representing the common preview elements.
     */
    const createCommonPreviewElements = (previewInfo): JSX.Element => {
        return (
            <React.Fragment>
                <SeaEpEpisodeTitle
                    seasonNumber={previewInfo.seasonNumber}
                    episodeNumber={previewInfo.episodeNumber}
                    episodeTitle={previewInfo.episodeTitle || previewInfo.title}
                    showType={previewInfo.previewType}
                />
                <ContentDescription
                    description={previewInfo.description}
                    videoResolution={
                        previewInfo.channel?.videoResolution || previewInfo.videoResolution
                    }
                    hasCc={previewInfo.hasCc}
                    isThreeD={previewInfo.isThreeD}
                    hasSap={previewInfo.hasSap}
                />
                <ParentalControlRatingAndGenres
                    internalRatings={previewInfo.internalRatingList}
                    genres={previewInfo.categoryLabel}
                />
                <AiringDateTimeDuration
                    duration={previewInfo.duration}
                    starRating={previewInfo.starRating}
                    firstAiredDate={previewInfo.firstAiredDate}
                />
            </React.Fragment>
        );
    };

    /**
     * Creates preview elements based on the provided preview information.
     *
     * @param previewInfo - The information used to create the preview elements.
     * @returns A JSX element representing the preview elements.
     */
    const createPreviewElements = (previewInfo): JSX.Element => {
        return (
            <React.Fragment>
                <ThuzzRating criticRating={previewInfo.criticRating} isLive={previewInfo.isLive} />
                <StatusMessage
                    entitlementAndAvailabilityStatus={previewInfo.entitlementAndAvailabilityStatus}
                    status={previewInfo.recordingStatus}
                    isKeepForeverRecording={previewInfo.isKeepForeverRecording}
                    showType={previewInfo.previewType}
                    isPartialRecording={previewInfo.isPartialRecording}
                    startTime={previewInfo.startTime}
                    endTime={previewInfo.endTime}
                    duration={previewInfo.recordingDuration}
                    recordingAvailability={previewInfo.recordingAvailability}
                />
                <TrickPlayRestriction
                    restrictions={previewInfo.trickPlayRestrictionList}
                    isLive={previewInfo.isLive}
                />
                {createTimeAndChannelAiringInfoElements(previewInfo)}
                <ContentPreviewTitle
                    title={previewInfo.title}
                    showType={previewInfo.previewType}
                    year={previewInfo.movieYear}
                />
                {createCommonPreviewElements(previewInfo)}
                <ActiorsInBillingOrder creditString={previewInfo.creditString} />
            </React.Fragment>
        );
    };

    /**
     * Creates preview elements based on the provided preview information.
     *
     * @param previewInfo - The information used to create the preview elements.
     * @returns A JSX element representing the preview elements.
     */
    const createTPreviewElements = (previewInfo): JSX.Element => {
        return (
            <React.Fragment>
                <StatusMessage
                    entitlementAndAvailabilityStatus={previewInfo.entitlementAndAvailabilityStatus}
                />
                {createTimeAndChannelAiringInfoElements(previewInfo)}
                {createCommonPreviewElements(previewInfo)}
            </React.Fragment>
        );
    };

    /**
     * A functional component that renders the content preview kernel.
     * Part 1 - teams and league
     * Part 2 - "<series title>"
     * Part 3 - "<series description> <button INFO icon>"
     * Part 4 - "<parental control rating> <genres>"
     *
     * @returns A JSX element representing the content preview kernel.
     */
    const ContentPreviewKernel = () => {
        const shouldDrawPreview =
            previewKernelType === PreviewKernelType.ContentPreviewKernel && contentPreviewInfo;
        if (!shouldDrawPreview) return null;

        const { onePassIndicatorList, previewType } = contentPreviewInfo;
        let league;
        let targetOnePassItem;
        let onePassCandidates: OnePassItem[] = [];
        let { description, title } = contentPreviewInfo;

        // eslint-disable-next-line react/prop-types
        if (onePassIndicatorList && onePassIndicatorList.length) {
            [, , , league] = onePassIndicatorList;
            // eslint-disable-next-line react/prop-types
            targetOnePassItem = onePassIndicatorList.find((item) => item.hasOnePass === false);
            // eslint-disable-next-line react/prop-types
            onePassCandidates = onePassIndicatorList?.filter((item) => item.hasOnePass === false);

            title = targetOnePassItem?.title || contentPreviewInfo.title;
            description =
                contentPreviewInfo.title === targetOnePassItem?.title
                    ? contentPreviewInfo.description
                    : oPreviewAssets.getStringAssestById(
                          PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
                      );
        }

        const vodPreviewFormatter = new VodPreviewFormatter(
            contentPreviewInfo,
            VodScreenType.VOD_PROGRAM_SCREEN,
        );
        const entitlementAndAvailabilityStatus = vodPreviewFormatter.getCombinedStatusMessage();

        if (
            previewType !== PreviewType.GamePreview &&
            previewType !== PreviewType.SportEventPreview &&
            previewType !== PreviewType.MatchPreview
        )
            return createPreviewElements({
                ...contentPreviewInfo,
                entitlementAndAvailabilityStatus,
            });

        return (
            <React.Fragment>
                {entitlementAndAvailabilityStatus ? (
                    <StatusMessage
                        entitlementAndAvailabilityStatus={entitlementAndAvailabilityStatus}
                    />
                ) : null}
                {onePassCandidates?.length > 1 ? (
                    <TeamSeriesAndLeague
                        onePassIndicatorList={onePassIndicatorList}
                        league={league}
                    />
                ) : (
                    <React.Fragment>
                        <TeamSeriesAndLeague
                            onePassIndicatorList={onePassIndicatorList}
                            league={league}
                            skipIfTitleMatch={targetOnePassItem?.title}
                        />
                        {onePassIndicatorList ? <br /> : null}
                        <ContentPreviewTitle title={title} />
                        <ContentDescription
                            description={description}
                            videoResolution={
                                contentPreviewInfo.channel?.videoResolution ||
                                contentPreviewInfo.videoResolution
                            }
                            hasCc={contentPreviewInfo.hasCc}
                            isThreeD={contentPreviewInfo.isThreeD}
                        />
                        <ParentalControlRatingAndGenres
                            internalRatings={contentPreviewInfo.internalRatingList}
                            genres={contentPreviewInfo.categoryLabel}
                        />
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    };

    /**
     * A functional component that renders the offer preview kernel.
     *
     * @returns A JSX element representing the offer preview kernel.
     */
    const OfferPreviewKernel = () => {
        const shouldDrawPreview =
            previewKernelType === PreviewKernelType.OfferPreviewKernel && offerPreviewInfo;
        if (!shouldDrawPreview) return null;
        const { offerPreview } = offerPreviewInfo;
        const { contentPreview } = offerPreview;
        const vodPreviewFormatter = new VodPreviewFormatter(
            contentPreview,
            VodScreenType.VOD_PROGRAM_SCREEN,
        );
        const oCombinedPreviewInfo = {
            ...offerPreview,
            ...contentPreview,
            entitlementAndAvailabilityStatus: vodPreviewFormatter.getCombinedStatusMessage(),
        };

        return createPreviewElements(oCombinedPreviewInfo);
    };

    /**
     * A functional component that renders the OnePass options preview kernel.
     *
     * @returns A JSX element representing the OnePass options preview kernel.
     */
    const OnePassOptionsPreviewKernel = () => {
        const shouldDrawPreview =
            previewKernelType === PreviewKernelType.OnePassOptionsPreviewKernel &&
            onePassOptionsPreviewInfo;
        if (!shouldDrawPreview) return null;

        const { preview } = onePassOptionsPreviewInfo;
        const {
            ChannelRecordingLabel,
            IncludeLabel,
            KeepAtMostLabel,
            KeepUntilLabel,
            RecordTypeLabel,
            RentOrBuyLabel,
            StartFromLabel,
            StartRecordingEarlyLabel,
            StopRecordingLateLabel,
            TitleLabel,
            VideoQualityLabel,
        } = preview;

        // "Include:"
        const sInclude = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_INCLUDE,
        );
        // "Start from:"
        const sStartFrom = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_START_FROM,
        );
        // "Rent or buy:"
        // (Do not display if "Rent or buy:" is not included in #RECORDING OPTIONS MENU ITEMS#.)
        const sRentOrBuy = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_RENT_OR_BUY,
        );
        // "Record:"
        const sRecord = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_RECORD_TYPE,
        );
        // "Channel:"
        const sChannel = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_CHANNEL,
        );
        // "Video quality:" (Only used if Hydra version > 1.0)
        const sVideoQuality = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_VIDEO_QUALITY,
        );
        // "Keep at most:"
        const sKeepAtMost = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_KEEP_AT_MOST,
        );
        // "Keep until:"
        const sKeepUntil = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_KEEP_UNTIL,
        );

        // "Start / Stop:"
        const sStartStop = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_START_STOP,
        );

        return (
            <React.Fragment>
                <div className="onepass-name">{TitleLabel}</div>
                {IncludeLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sInclude}:</span>
                        <span className="value">{IncludeLabel}</span>
                    </div>
                ) : null}
                {StartFromLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sStartFrom}:</span>
                        <span className="value">{StartFromLabel}</span>
                    </div>
                ) : null}
                {RentOrBuyLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sRentOrBuy}:</span>
                        <span className="value">{RentOrBuyLabel}</span>
                    </div>
                ) : null}
                {RecordTypeLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sRecord}:</span>
                        <span className="value">{RecordTypeLabel}</span>
                    </div>
                ) : null}
                {ChannelRecordingLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sChannel}:</span>
                        <span className="value">{ChannelRecordingLabel}</span>
                    </div>
                ) : null}
                {VideoQualityLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sVideoQuality}:</span>
                        <span className="value">{VideoQualityLabel}</span>
                    </div>
                ) : null}
                {KeepAtMostLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sKeepAtMost}:</span>
                        <span className="value">{KeepAtMostLabel}</span>
                    </div>
                ) : null}
                {KeepUntilLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sKeepUntil}:</span>
                        <span className="value">{KeepUntilLabel}</span>
                    </div>
                ) : null}
                {StartRecordingEarlyLabel && StopRecordingLateLabel ? (
                    <div className="preview-label-value">
                        <span className="label">{sStartStop}:</span>
                        <span className="value">
                            {StartRecordingEarlyLabel} / {StopRecordingLateLabel}
                        </span>
                    </div>
                ) : null}
            </React.Fragment>
        );
    };

    /**
     * A functional component that renders the recording options preview kernel.
     *
     * @returns A JSX element representing the recording options preview kernel.
     */
    const RecordingOptionsPreviewKernel = () => {
        const shouldDrawPreview =
            previewKernelType === PreviewKernelType.RecordingOptionsPreviewKernel &&
            recordingOptionsPreviewInfo;
        if (!shouldDrawPreview) return null;

        const { keepUntilLabel, startRecordingLabel, stopRecordingLabel, timeAndChannelLabel } =
            recordingOptionsPreviewInfo;
        const sKeepUntil = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_KEEP_UNTIL,
        );
        const sStartRec = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_START_REC,
        );
        const sStopRec = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_STOP_REC,
        );
        const sTimeAndChannel = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.PREVIEW_LABEL_TIME_AND_CHANNEL,
        );

        return (
            <React.Fragment>
                <div className="preview-label-value">
                    <span className="label">{sTimeAndChannel}:</span>
                    <span className="value">{timeAndChannelLabel}</span>
                </div>
                <div className="preview-label-value">
                    <span className="label">{sKeepUntil}:</span>
                    <span className="value">{keepUntilLabel}</span>
                </div>
                <div className="preview-label-value">
                    <span className="label">{sStartRec}:</span>
                    <span className="value">{startRecordingLabel}</span>
                </div>
                <div className="preview-label-value">
                    <span className="label">{sStopRec}:</span>
                    <span className="value">{stopRecordingLabel}</span>
                </div>
            </React.Fragment>
        );
    };

    /**
     * A functional component that renders the recording preview kernel.
     *
     * @returns A JSX element representing the recording preview kernel.
     */
    const RecordingPreviewKernel = () => {
        const contentPreview = recordingPreviewInfo?.offerPreview.contentPreview;
        const shouldDrawPreview =
            previewKernelType === PreviewKernelType.RecordingPreviewKernel && recordingPreviewInfo;
        if (!shouldDrawPreview) return null;
        const vodPreviewFormatter = new VodPreviewFormatter(
            contentPreview,
            VodScreenType.VOD_PROGRAM_SCREEN,
        );
        const oCombinedPreviewInfo = {
            ...contentPreview,
            entitlementAndAvailabilityStatus: vodPreviewFormatter.getCombinedStatusMessage(),
        };
        return createPreviewElements(oCombinedPreviewInfo);
    };

    /**
     * A functional component that renders the "All Upcoming Episodes" kernel.
     *
     * @returns A JSX element representing the "All Upcoming Episodes" kernel.
     */
    const TAllUpcomingEpisodesKernel = () => {
        const shouldDrawPreview =
            previewKernelType === TPreviewKernelType.TAllUpcomingEpisodesKernel && offerPreviewInfo;
        if (!shouldDrawPreview) return null;
        const { offerPreview } = offerPreviewInfo;
        const { contentPreview } = offerPreview;
        const vodPreviewFormatter = new VodPreviewFormatter(
            contentPreview,
            VodScreenType.VOD_PROGRAM_SCREEN,
        );
        const oCombinedPreviewInfo = {
            ...offerPreview,
            ...contentPreview,
            entitlementAndAvailabilityStatus: vodPreviewFormatter.getCombinedStatusMessage(),
        };

        return createTPreviewElements(oCombinedPreviewInfo);
    };

    /**
     * A functional component that renders the "Upcoming Airings" kernel.
     *
     * @returns A JSX element representing the "Upcoming Airings" kernel.
     */
    const TUpcomingAiringsKernel = () => {
        const shouldDrawPreview =
            previewKernelType === TPreviewKernelType.TUpcomingAiringsKernel && offerPreviewInfo;
        if (!shouldDrawPreview) return null;
        const { offerPreview } = offerPreviewInfo;
        const { contentPreview } = offerPreview;
        const vodPreviewFormatter = new VodPreviewFormatter(
            contentPreview,
            VodScreenType.VOD_PROGRAM_SCREEN,
        );
        const oCombinedPreviewInfo = {
            ...offerPreview,
            ...contentPreview,
            entitlementAndAvailabilityStatus: vodPreviewFormatter.getCombinedStatusMessage(),
        };

        return createTPreviewElements(oCombinedPreviewInfo);
    };

    /**
     * A functional component that renders the unified collection and OnePass options preview
     * kernel.
     *
     * @returns A JSX element representing the unified collection and OnePass options preview
     * kernel.
     */
    const UnifiedCollectionAndOnePassOptionsPreviewKernel = () => {
        const shouldDrawPreview =
            previewKernelType ===
                PreviewKernelType.UnifiedCollectionAndOnePassOptionsPreviewKernel &&
            unicAndOnePassOptionsPreviewInfo;
        if (!shouldDrawPreview) return null;

        // TODO: Implement the logic to create the preview elements for the unified collection and
        // OnePass options preview kernel.
        // Currently, this function returns an empty fragment to prevent a application error.
        return <React.Fragment></React.Fragment>;
    };

    /**
     * A functional component that renders the unified collection preview kernel.
     *
     * @returns A JSX element representing the unified collection preview kernel.
     */
    const UnifiedCollectionPreviewKernel = () => {
        const shouldDrawPreview =
            previewKernelType === PreviewKernelType.UnifiedCollectionPreviewKernel &&
            unicPreviewInfo;
        if (!shouldDrawPreview) return null;
        return createPreviewElements(unicPreviewInfo);
    };

    /**
     * A functional component that renders the VOD offer preview kernel.
     *
     * @returns A JSX element representing the VOD offer preview kernel.
     */
    const VodOfferPreviewKernel = () => {
        const shouldDrawPreview =
            previewKernelType === PreviewKernelType.VodOfferPreviewKernel && vodOfferPreviewInfo;
        if (!shouldDrawPreview) return null;

        const { vodOfferPreview } = vodOfferPreviewInfo;
        const { contentPreview } = vodOfferPreview;
        const vodPreviewFormatter = new VodPreviewFormatter(
            contentPreview,
            VodScreenType.VOD_PROGRAM_SCREEN,
        );
        const oCombinedPreviewInfo = {
            ...vodOfferPreview,
            ...contentPreview,
            entitlementAndAvailabilityStatus: vodPreviewFormatter.getCombinedStatusMessage(),
        };
        return createPreviewElements(oCombinedPreviewInfo);
    };

    /**
     * Processes the preview kernel to determine which preview kernel type is being displayed and
     * sets the preview information for the corresponding preview kernel type.
     *
     * @param aPreviewKernel - The preview kernel to process.
     */
    const processPreviewKernel = async (aPreviewKernel: PreviewKernel | TPreviewKernel) => {
        setPreviewKernelType(aPreviewKernel.previewKernelType);
        const isVodPreviewEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_VOD_PREVIEW_REMOVE_IN_KST_14762,
        );
        switch (aPreviewKernel.previewKernelType) {
            case PreviewKernelType.ContentPreviewKernel:
                if (isVodPreviewEnabled) {
                    setContentPreviewInfo(await useCases.getContentPreviewV3Info());
                    break;
                }
                setContentPreviewInfo(await useCases.getContentPreviewInfo());
                break;

            case PreviewKernelType.OfferPreviewKernel:
                setOfferPreviewInfo(await useCases.getOfferPreviewInfo());
                break;

            case PreviewKernelType.OnePassOptionsPreviewKernel:
                setOnePassOptionsPreviewInfo(await useCases.getOnePassOptionsPreviewInfo());
                break;

            case PreviewKernelType.RecordingOptionsPreviewKernel:
                setRecordingOptionsPreviewInfo(await useCases.getRecordingOptionsPreviewInfo());
                break;

            case PreviewKernelType.RecordingPreviewKernel:
                setRecordingPreviewInfo(await useCases.getRecordingPreviewInfo());
                break;

            case PreviewKernelType.UnifiedCollectionAndOnePassOptionsPreviewKernel:
                setUnicAndOnePassOptionsPreviewInfo(
                    await useCases.getUnifiedCollectionAndOnePassOptionsPreviewInfo(),
                );
                break;

            case PreviewKernelType.UnifiedCollectionPreviewKernel:
                setUnicPreviewInfo(await useCases.getUnifiedCollectionPreviewInfo());
                break;

            case PreviewKernelType.VodOfferPreviewKernel:
                if (isVodPreviewEnabled) {
                    setVodOfferPreviewInfo(await useCases.getVodOfferPreviewV2Info());
                    break;
                }
                break;

            case TPreviewKernelType.TAllEpisodesKernel:
            case TPreviewKernelType.TAllUpcomingEpisodesKernel:
            case TPreviewKernelType.TUpcomingAiringsKernel:
                try {
                    setOfferPreviewInfo(await useCases.getOfferPreviewInfo());
                } catch (error) {
                    setOfferPreviewInfo(undefined);
                }
                break;

            default:
        }
    };

    useEffect(() => {
        const timeoutRef = setTimeout(() => {
            processPreviewKernel(previewKernel);
        }, PREVIEW_DEBOUNCE_TIME);

        return () => {
            clearTimeout(timeoutRef);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewKernel]);

    return (
        <React.Fragment>
            <div className="content-preview-container">
                <ContentPreviewKernel />
                <OfferPreviewKernel />
                <OnePassOptionsPreviewKernel />
                <RecordingOptionsPreviewKernel />
                <RecordingPreviewKernel />
                <TAllUpcomingEpisodesKernel />
                <TUpcomingAiringsKernel />
                <UnifiedCollectionAndOnePassOptionsPreviewKernel />
                <UnifiedCollectionPreviewKernel />
                <VodOfferPreviewKernel />
            </div>
        </React.Fragment>
    );
};

export default ContentPreviewComponent;
